import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { useGetBank } from 'src/services/bank.service';
import { Bank } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import AccountType from 'src/components/AccountType';
import BankActionMenuButton from 'src/components/ActionButtons/BankActionMenu';

const BankGrid: FC<{ state: boolean; searchTerm: string }> = ({
  state,
  searchTerm,
}) => {
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const { loading, banks, fetchBanks, deleteBank, totalItems } = useGetBank();
  const [sortBy, setSortBy] = useState<string>('bankName');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'bankName', sort: 'asc' },
  ]);

  useEffect(() => {
    fetchBanks(page, pageSize, searchTerm, sortBy, order);
  }, [state, page, pageSize, searchTerm, sortBy, order]);
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    const order = (newSortModel[0]?.sort || 'asc').toUpperCase();
    setPage(1);
    setSortBy(newSortModel[0]?.field || 'bankName');
    setOrder(order as 'ASC' | 'DESC');
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    { field: 'bankName', headerName: 'Bank Name', flex: 0.8 },
    {
      field: 'branchName',
      headerName: 'Branch',
      flex: 0.75,
    },
    {
      field: 'accountNumber',
      headerName: 'Account Number',
      flex: 0.6,
      sortable: false,
    },
    { field: 'accountName', headerName: 'Account Name', flex: 0.75 },
    {
      field: 'accountType',
      headerName: 'Account Type',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <AccountType accountType={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <BankActionMenuButton
          rowId={params.id}
          accountNumber={params.row.accountNumber}
          status={params.row.status}
          fetchBanks={() =>
            fetchBanks(page, pageSize, searchTerm, sortBy, order)
          }
          deleteBank={deleteBank}
        />
      ),
    },
  ];

  const transformData = (banks: Bank[]) => {
    return banks.map((bank, index) => ({
      id: bank.id,
      number: (page - 1) * pageSize + index + 1,
      bankName: bank.bankName,
      branchName: bank.branchName,
      accountNumber: bank.accountNumber,
      accountName: bank.accountName,
      accountType: bank.accountType,
      status: bank.status,
    }));
  };

  const rows = transformData(banks);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        sortModel={sortModel}
        columns={columns}
        disableColumnMenu
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        sortingOrder={['asc', 'desc']}
      />
    </StyledGridContainer>
  );
};

export default BankGrid;
